import React from 'react';
import type { LucideIcon } from 'lucide-react';

interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  className = '',
  icon: Icon,
  iconPosition = 'right',
  fullWidth = false,
}) => {
  const baseClasses = 'rounded-full font-medium transition-all duration-200 transform hover:scale-105 active:scale-95 flex items-center justify-center';
  
  const variantClasses = {
    primary: 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white',
    secondary: 'bg-white/5 hover:bg-white/10 text-white outline outline-1 outline-white/20',
    outline: 'bg-transparent hover:bg-white/5 text-white outline outline-1 outline-white/20',
    ghost: 'bg-transparent hover:bg-white/5 text-white',
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs sm:text-sm h-8 sm:h-9',
    md: 'px-4 py-2 text-sm sm:text-base h-10 sm:h-11',
    lg: 'px-5 sm:px-6 py-2.5 sm:py-3 text-base sm:text-lg h-12 sm:h-14',
  };

  const widthClass = fullWidth ? 'w-full' : '';
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed hover:scale-100' : '';

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${disabledClasses} ${widthClass} ${className}`}
    >
      {Icon && iconPosition === 'left' && <Icon size={16} className="mr-1.5 sm:mr-2 flex-shrink-0" />}
      <span className="truncate">{children}</span>
      {Icon && iconPosition === 'right' && <Icon size={16} className="ml-1.5 sm:ml-2 flex-shrink-0" />}
    </button>
  );
};